/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  getShareProperties,
  useCurrentQueueItem,
  usePlayer,
} from "@packages/media";
import { useBranch } from "@packages/sdk";
import type { WithButtonWrapper } from "@packages/ui";
import {
  constantSemanticColors,
  HideFromKids,
  IconButton,
  numericPercentages,
  ShareIcon,
  ShareModal,
  spacing,
  useDynamicViewport,
  useModal,
  zIndices,
} from "@packages/ui";
import { useContext, useEffect } from "react";

import {
  coreMediaStyles,
  useMediaClass,
} from "../../../../utils/media.emotion";
import { PlayerInternalContext } from "../../../core/MediaPlayer.emotion";
import { Queue } from "../../../core/Queue.emotion";
import { TextOnScreen } from "../../../core/TextOnScreen/TextOnScreen.emotion";
import { FragmentAsChild } from "../../../primitives/FragmentAsChild";
import { VolumeControl } from "../../controls/VolumeControl.emotion";
import { MediaMenu } from "../MediaMenu.emotion";
import { SubtitlesButton } from "./SubtitlesButton.emotion";

const controlSetStyle: Interpolation = {
  alignItems: "center",
  columnGap: spacing.s,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  justifyContent: "flex-end",
  pointerEvents: "all",
};
const fullWidthStyle: Interpolation = {
  width: numericPercentages[100],
};
const smallContainerStyle: Interpolation = {
  justifyContent: "space-between",
};
const withVolumeStyle: Interpolation = {
  justifyContent: "space-between",
};
const leftSetStyle: Interpolation = {
  justifyContent: "flex-start",
};
const modalZStyle: Interpolation = {
  zIndex: zIndices.modalOverQueue,
};
const modalPaddingStyle: Interpolation = {
  paddingTop: spacing.l,
};
const spreadSetStyle: Interpolation = {
  justifyContent: "space-between",
};

export type SettingsControlsProps = WithButtonWrapper<{
  size?: "s" | "l";
  withVolume?: boolean;
  showControls?: boolean;
}>;

export const SettingsControls = ({
  size = "l",
  withVolume = false,
  Wrapper = FragmentAsChild,
  showControls = true,
}: SettingsControlsProps) => {
  const player = usePlayer();
  const { currentItem } = useCurrentQueueItem();
  const {
    collapsePlayer,
    popupRef,
    transcriptOpen,
    transcriptOpenChangeHandler,
  } = useContext(PlayerInternalContext);
  const shareModal = useModal(ShareModal);
  const branch = useBranch();
  const analytics = useAnalytics();
  const isDesktop = useDynamicViewport({ minimumWidth: 1024 });
  const mediaClass = useMediaClass();

  useEffect(() => {
    if (
      (mediaClass.includes("expanded") || mediaClass.includes("fullscreen")) &&
      mediaClass.includes("video") &&
      transcriptOpen
    ) {
      transcriptOpenChangeHandler(false);
    }
    // transcriptOpen is a dependency here, but we don't want this useEffect to run when transcriptOpen changes,
    // ONLY when mediaClass changes, so that expanding/maximizing a video closes the transcript, but opening
    // the transcript doesn't close the transcript
  }, [mediaClass]);

  const ShareAndSettings = (
    <>
      <HideFromKids>
        <Wrapper asChild>
          <IconButton
            icon={<ShareIcon fill={constantSemanticColors.onColorVeryHigh} />}
            variant={"transparentWhite"}
            tabIndex={0}
            onClick={async () => {
              const event =
                currentItem?.type === "prayer"
                  ? "Tapped Share Prayer"
                  : currentItem?.type === "radio_song"
                    ? "Tapped Share Radio Station"
                    : null;
              if (event) {
                const props: Record<string, number> = {};
                if (currentItem.type === "prayer") {
                  props.prayer = currentItem.prayer.id;
                } else if (currentItem.type === "radio_song") {
                  props.radio_station = currentItem.radio_station.id;
                }
                analytics.track({
                  event,
                  ...props,
                });
              }
              const shareProps = await getShareProperties(currentItem, branch, [
                modalZStyle,
                modalPaddingStyle,
              ]);
              return shareModal.show({
                ...shareProps,
                overlayProps: {
                  onPointerDown: () => {
                    analytics.track({
                      event: HallowAnalyticsEvent.ClosedShareAudioView,
                    });
                  },
                },
                portalProps: { container: popupRef.current },
              });
            }}
          />
        </Wrapper>
      </HideFromKids>
      <MediaMenu Wrapper={Wrapper} showControls={showControls} />
    </>
  );

  const TextAndQueue =
    currentItem?.type === "radio_song" ? null : (
      <>
        {mediaClass.includes("video") && (
          <SubtitlesButton showControls={showControls} Wrapper={Wrapper} />
        )}
        <TextOnScreen
          id={currentItem?.selected_audio?.id}
          prayerId={currentItem?.prayer?.id}
          iconColor={constantSemanticColors.onColorVeryHigh}
          Wrapper={Wrapper}
          onClick={(e) => {
            if (
              mediaClass.includes("expanded") ||
              mediaClass.includes("fullscreen")
            )
              collapsePlayer(e);
          }}
          open={transcriptOpen}
          onOpenChange={transcriptOpenChangeHandler}
        />
        <Queue Wrapper={Wrapper} />
      </>
    );

  return (
    <div
      css={[
        controlSetStyle,
        coreMediaStyles.unclickable,
        fullWidthStyle,
        size === "s" ? smallContainerStyle : null,
        withVolume ? withVolumeStyle : null,
      ]}
      onClick={(e) => {
        if (e.currentTarget !== e.target) e.stopPropagation();
      }}
    >
      {withVolume ? (
        <>
          <div css={[controlSetStyle, leftSetStyle]}>
            {ShareAndSettings}
            <VolumeControl
              value={player?.volume ?? 1}
              onVolumeChange={(v) => player?.setVolume(v)}
              iconColor={"onColorVeryHigh"}
              onOverlay={true}
              Wrapper={Wrapper}
            />
          </div>
          <div css={controlSetStyle}>{TextAndQueue}</div>
        </>
      ) : (
        <div
          css={[
            controlSetStyle,
            !isDesktop && ["expanded", "fullscreen"].includes(player?.display)
              ? [fullWidthStyle, spreadSetStyle]
              : [],
          ]}
        >
          {ShareAndSettings}
          {TextAndQueue}
        </div>
      )}
    </div>
  );
};
